import React, { useState } from "react";
import axios from "axios";
import { Icon } from "./Icon";

interface SupportProps {
  user: {
    id: string;
    username: string;
    displayName?: string;
    display_name?: string;
    email?: string;
    userType: "user" | "premium" | "vip" | "admin";
  } | null;
  authToken: string;
}

const Support: React.FC<SupportProps> = ({ user, authToken }) => {
  const [formData, setFormData] = useState({
    subject: "",
    category: "general",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !formData.subject || !formData.message) return;

    setSubmitting(true);
    try {
      await axios.post(
        "/api/support/ticket",
        {
          subject: formData.subject,
          category: formData.category,
          message: formData.message,
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      setSubmitted(true);
      setFormData({
        subject: "",
        category: "general",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting support ticket:", error);
      alert("Failed to submit support ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "0 20px",
          minHeight: "60vh",
          alignItems: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "40px",
            borderRadius: "8px",
            textAlign: "center",
            maxWidth: "500px",
          }}
        >
          <h3 style={{ color: "#e2e8f0", marginBottom: "16px" }}>
            Login Required
          </h3>
          <p style={{ color: "#cbd5e0", marginBottom: "20px" }}>
            Please log in to submit a support ticket.
          </p>
          <a
            href="/auth/twitch"
            style={{
              display: "inline-block",
              padding: "12px 24px",
              backgroundColor: "#9146FF",
              color: "white",
              textDecoration: "none",
              borderRadius: "6px",
              fontWeight: "500",
            }}
          >
            Login with Twitch
          </a>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "0 20px",
          minHeight: "60vh",
          alignItems: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "40px",
            borderRadius: "8px",
            textAlign: "center",
            maxWidth: "500px",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <Icon name="check" size={48} color="#10B981" />
          </div>
          <h3 style={{ color: "#e2e8f0", marginBottom: "16px" }}>
            Ticket Submitted!
          </h3>
          <p style={{ color: "#cbd5e0", marginBottom: "20px" }}>
            Thank you for contacting us. We&apos;ve received your support ticket
            and will get back to you as soon as possible.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            style={{
              padding: "12px 24px",
              backgroundColor: "#9146FF",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Submit Another Ticket
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        gap: "20px",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
      }}
    >
      <div
        style={{
          flex: "1",
          maxWidth: isMobile ? "100%" : "800px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{ fontSize: "20px", margin: "0 0 24px 0", color: "#e2e8f0" }}
        >
          Support & Contact
        </h3>

        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "24px",
            borderRadius: "8px",
            marginBottom: "20px",
            border: "1px solid #4a5568",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <h4
            style={{
              marginTop: 0,
              marginBottom: "16px",
              color: "#9146FF",
              fontSize: "18px",
            }}
          >
            Submit a Support Ticket
          </h4>

          <form
            onSubmit={handleSubmit}
            style={{ display: "grid", gap: "16px" }}
          >
            <div style={{ position: "relative" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "8px 32px 8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  boxSizing: "border-box",
                }}
              >
                <option value="general">General Support</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing & Subscriptions</option>
                <option value="feature">Feature Request</option>
                <option value="bug">Bug Report</option>
                <option value="account">Account Issues</option>
              </select>
              <div
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  marginTop: "10px",
                  pointerEvents: "none",
                  color: "#cbd5e0",
                  fontSize: "12px",
                }}
              >
                ▼
              </div>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Subject
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                placeholder="Brief description of your issue"
                required
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                Message
              </label>
              <textarea
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                placeholder="Please provide detailed information about your issue..."
                required
                rows={6}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !formData.subject || !formData.message}
              style={{
                padding: "12px 24px",
                backgroundColor:
                  submitting || !formData.subject || !formData.message
                    ? "#4a5568"
                    : "#9146FF",
                color:
                  submitting || !formData.subject || !formData.message
                    ? "#718096"
                    : "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: "500",
                cursor:
                  submitting || !formData.subject || !formData.message
                    ? "not-allowed"
                    : "pointer",
                justifySelf: "start",
              }}
            >
              {submitting ? "Submitting..." : "Submit Ticket"}
            </button>
          </form>
        </div>
      </div>

      {/* Support Info Panel */}
      <div
        style={{
          width: isMobile ? "100%" : "350px",
          maxWidth: isMobile ? "800px" : "350px",
          backgroundColor: "rgba(45, 55, 72, 0.95)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          borderRadius: "8px",
          padding: "20px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          height: "fit-content",
          position: isMobile ? "static" : "sticky",
          top: "80px",
          order: isMobile ? 2 : 0,
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{
            color: "#e2e8f0",
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "18px",
          }}
        >
          <Icon
            name="message-square"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Support Information
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="phone"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Response Times
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            We typically respond to support tickets within 24-48 hours during
            business days.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="tag"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Categories
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>Technical:</strong> Bot not responding, connection issues
            </li>
            <li>
              <strong>Billing:</strong> Subscription & payment questions
            </li>
            <li>
              <strong>Feature:</strong> Request new functionality
            </li>
            <li>
              <strong>Bug:</strong> Report software issues
            </li>
          </ul>
        </div>

        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "12px",
            borderRadius: "6px",
            marginTop: "15px",
            border: "1px solid #4a5568",
          }}
        >
          <p
            style={{
              color: "#9146FF",
              fontSize: "12px",
              margin: "0 0 6px 0",
              fontWeight: "bold",
            }}
          >
            <Icon
              name="lightbulb"
              size={12}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Before Submitting:
          </p>
          <ul
            style={{
              color: "#a0aec0",
              fontSize: "11px",
              margin: 0,
              paddingLeft: "16px",
            }}
          >
            <li>Check if HAL is modded in your channel</li>
            <li>Try refreshing the dashboard</li>
            <li>Include specific error messages</li>
            <li>Mention your browser if it&apos;s a web issue</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Support;
